/**
 * The booking steps a reply can carry (ADR-110): the assistant takes the
 * appointment inside the conversation, and the widget draws each step as
 * something to tap or fill in rather than something to type.
 *
 * A `/chat/message` body — the REST response, or the stream's `done` event,
 * which is the same object — may carry `actions` next to `reply`. This module
 * reads them and works out what each one shows and sends. It is pure (no
 * React, no fetch) so scripts/test-config.mjs can pin the rules against the
 * built artefact, like lead.ts.
 *
 * What goes back is the SAME request the widget always sends, with `message`
 * set to what the step asks for:
 *
 *   ask option / offer_slots slot   the option's `value` / the slot's instant, byte for byte —
 *                                   the instant is the booking's lock key: never reformat it
 *   collect_details                 a short localised line ("Booking under Ada") plus a
 *                                   top-level `details` object; the email is never in the line
 *   confirm                         exactly `yes` or `no`, whatever the widget's language
 *
 * The bubble the visitor sees is the human label, never the raw value.
 *
 * Tolerance. The vocabulary is additive (ADR-043 §4.4): an unknown `type` is
 * ignored and the reply text stands on its own, and unknown keys on a known
 * type are ignored. A known type missing a field the contract names, or
 * carrying it with the wrong type, is dropped whole; an optional field of the
 * wrong type (`options`, `timezone`) reads as absent. Lists are filtered
 * element by element — a slot that is not an instant, an option without a
 * value — then capped, and a list the widget draws that ends up empty drops
 * its action. Nothing is trimmed or rewritten: strings stay verbatim.
 */
import { CONTACT_LIMITS, validateContact, type ContactField, type ContactValues } from './details';
import { fill, type WidgetStrings } from './strings';

/**
 * Every action type this widget handles — drawn, or knowingly drawn as
 * nothing (`handoff`, `end`). The backend's outcome test reads this constant
 * from this file by name: keep it ONE literal array assignment.
 */
export const RENDERED_ACTIONS = ['ask', 'offer_slots', 'collect_details', 'confirm', 'booked', 'capture_lead', 'handoff', 'end'] as const;

export type ChatActionType = (typeof RENDERED_ACTIONS)[number];

/** Slot chips per `offer_slots`: the backend's own cap (MAX_OFFERED_SLOTS). */
export const MAX_SLOTS = 6;

/** Option chips per `ask`. */
export const MAX_ASK_OPTIONS = 7;

/** The customer fields a `collect_details` form can show. */
export const DETAIL_FIELDS = ['customer_name', 'customer_email', 'customer_phone'] as const;

export type DetailField = (typeof DETAIL_FIELDS)[number];

/** Each `collect_details` field as the one details form names it (details.ts). */
export const CONTACT_FIELD_OF: Readonly<Record<DetailField, ContactField>> = {
  customer_name: 'name',
  customer_email: 'email',
  customer_phone: 'phone',
};

/** The canonical confirm answers: English words on the wire in every language. */
export const CONFIRM_YES = 'yes';
export const CONFIRM_NO = 'no';

export interface AskOption {
  value: string;
  /** A service's name. A date option's chip is formatted from its value instead. */
  label?: string;
}

export type ChatAction =
  | { type: 'ask'; field: string; prompt: string; options: AskOption[] }
  | { type: 'offer_slots'; slots: string[]; timezone?: string }
  | { type: 'collect_details'; purpose: 'booking'; fields: DetailField[]; required: DetailField[] }
  | { type: 'confirm'; of: 'booking'; summary: string }
  | { type: 'booked'; bookingId: string; service: string; start: string; timezone?: string }
  | { type: 'capture_lead'; fields: string[]; reason: string }
  | { type: 'handoff'; reason: string; urgent: boolean }
  | { type: 'end'; reason: string };

type Obj = Record<string, unknown>;

const isObject = (x: unknown): x is Obj => !!x && typeof x === 'object' && !Array.isArray(x);
const isString = (x: unknown): x is string => typeof x === 'string';
const isDetailField = (x: unknown): x is DetailField =>
  isString(x) && (DETAIL_FIELDS as readonly string[]).includes(x);

// ── Dates and instants ──────────────────────────────────────────

/** `YYYY-MM-DD`. */
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * An ISO 8601 instant WITH its offset — `2026-09-14T09:00:00Z`,
 * `2026-09-14T09:00:00.000+01:00`, `2026-09-14T09:00+0100`. Without an offset
 * a time names no instant at all, so it is not a slot.
 */
const INSTANT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/i;

/** Epoch ms of a calendar date and time read as UTC; null when a field is out of range (2026-02-30, 24:00). */
function utcMs(y: number, mo: number, d: number, h = 0, mi = 0, s = 0): number | null {
  const ms = Date.UTC(y, mo - 1, d, h, mi, s);
  const t = new Date(ms);
  const same =
    t.getUTCFullYear() === y &&
    t.getUTCMonth() === mo - 1 &&
    t.getUTCDate() === d &&
    t.getUTCHours() === h &&
    t.getUTCMinutes() === mi &&
    t.getUTCSeconds() === s;
  return same ? ms : null;
}

interface ParsedInstant {
  /** The instant, epoch ms. */
  ms: number;
  /** The wall clock as written, read as UTC: what the string's own offset shows. */
  wallMs: number;
  /** `Z`: the string carries no local offset of its own. */
  utc: boolean;
}

function parseInstant(value: string): ParsedInstant | null {
  const m = INSTANT_RE.exec(value);
  if (!m) return null;
  const wallMs = utcMs(+m[1], +m[2], +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
  if (wallMs === null) return null;
  const zone = m[7].toUpperCase();
  if (zone === 'Z') return { ms: wallMs, wallMs, utc: true };
  const digits = zone.slice(1).replace(':', '');
  const hours = +digits.slice(0, 2);
  const minutes = +digits.slice(2, 4);
  if (hours > 23 || minutes > 59) return null;
  const sign = zone[0] === '-' ? -1 : 1;
  return { ms: wallMs - sign * (hours * 60 + minutes) * 60_000, wallMs, utc: false };
}

function parseDate(value: string): number | null {
  const m = DATE_RE.exec(value);
  return m ? utcMs(+m[1], +m[2], +m[3]) : null;
}

const SLOT_FORMAT: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' };
const DATE_FORMAT: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };

function isTimeZone(timezone: string | undefined): timezone is string {
  if (!timezone) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/** A formatter in the page's language; the browser's own when Intl refuses the tag (`en_GB`). */
function formatter(language: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(language || undefined, options);
  } catch {
    return new Intl.DateTimeFormat(undefined, options);
  }
}

/**
 * A slot's chip, and the `booked` card's time: "Mon 14 Sept, 9:00" (en-GB) in
 * the page's language, in the action's IANA `timezone`. Without a usable
 * timezone, the instant's own offset as written (`…T09:00+01:00` reads 9:00) — or, for
 * a bare UTC `Z`, which says nothing about where the business is, the
 * visitor's own clock. A string that is not an instant comes back unchanged.
 */
export function formatSlotLabel(instant: string, language: string, timezone?: string): string {
  const parsed = parseInstant(instant);
  if (!parsed) return instant;
  if (isTimeZone(timezone)) return formatter(language, { ...SLOT_FORMAT, timeZone: timezone }).format(parsed.ms);
  if (!parsed.utc) return formatter(language, { ...SLOT_FORMAT, timeZone: 'UTC' }).format(parsed.wallMs);
  return formatter(language, SLOT_FORMAT).format(parsed.ms);
}

/**
 * A date option's chip: "Mon 14 Sept" for `2026-09-14`, the calendar day
 * itself — formatted at UTC midnight IN UTC, so no visitor's offset can move
 * it to the day before. A value that is not a real `YYYY-MM-DD` comes back
 * unchanged.
 */
export function formatDateLabel(value: string, language: string): string {
  const ms = parseDate(value);
  return ms === null ? value : formatter(language, { ...DATE_FORMAT, timeZone: 'UTC' }).format(ms);
}

// ── Parsing ─────────────────────────────────────────────────────

function parseOptions(raw: unknown, field: string): AskOption[] {
  if (!Array.isArray(raw)) return [];
  const out: AskOption[] = [];
  for (const option of raw) {
    if (out.length === MAX_ASK_OPTIONS) break;
    if (!isObject(option) || !isString(option.value) || !option.value) continue;
    if (field === 'date' && parseDate(option.value) === null) continue;
    out.push(isString(option.label) && option.label ? { value: option.value, label: option.label } : { value: option.value });
  }
  return out;
}

function optionalTimezone(raw: unknown): { timezone?: string } {
  return isString(raw) && raw ? { timezone: raw } : {};
}

function parseAction(raw: unknown): ChatAction | null {
  if (!isObject(raw)) return null;
  switch (raw.type) {
    case 'ask': {
      const { field, prompt } = raw;
      if (!isString(field) || !field || !isString(prompt)) return null;
      return { type: 'ask', field, prompt, options: parseOptions(raw.options, field) };
    }
    case 'offer_slots': {
      const { slots } = raw;
      if (!Array.isArray(slots)) return null;
      const valid = slots.filter((s): s is string => isString(s) && parseInstant(s) !== null).slice(0, MAX_SLOTS);
      return valid.length > 0 ? { type: 'offer_slots', slots: valid, ...optionalTimezone(raw.timezone) } : null;
    }
    case 'collect_details': {
      const { purpose, fields, required } = raw;
      // Only a booking's details: the form's line says "Booking under …".
      if (purpose !== 'booking' || !Array.isArray(fields) || !Array.isArray(required)) return null;
      const shown: DetailField[] = [];
      for (const f of fields) if (isDetailField(f) && !shown.includes(f)) shown.push(f);
      if (shown.length === 0) return null;
      return { type: 'collect_details', purpose, fields: shown, required: shown.filter((f) => required.includes(f)) };
    }
    case 'confirm':
      return raw.of === 'booking' && isString(raw.summary) ? { type: 'confirm', of: 'booking', summary: raw.summary } : null;
    case 'booked': {
      const { bookingId, service, start } = raw;
      if (!isString(bookingId) || !isString(service) || !isString(start) || !parseInstant(start)) return null;
      return { type: 'booked', bookingId, service, start, ...optionalTimezone(raw.timezone) };
    }
    case 'capture_lead':
      return Array.isArray(raw.fields) && isString(raw.reason)
        ? { type: 'capture_lead', fields: raw.fields.filter(isString), reason: raw.reason }
        : null;
    case 'handoff':
      return isString(raw.reason) && typeof raw.urgent === 'boolean' ? { type: 'handoff', reason: raw.reason, urgent: raw.urgent } : null;
    case 'end':
      return isString(raw.reason) ? { type: 'end', reason: raw.reason } : null;
    default:
      return null;
  }
}

/** The actions of one reply body's `actions`, as the widget can use them; anything else is dropped silently. */
export function parseActions(raw: unknown): ChatAction[] {
  if (!Array.isArray(raw)) return [];
  const out: ChatAction[] = [];
  for (const item of raw) {
    const action = parseAction(item);
    if (action) out.push(action);
  }
  return out;
}

/**
 * Whether an action draws something to tap or fill in. While one is shown the
 * ordinary suggestion chips step aside. `booked` informs, and `ask` without
 * options, `capture_lead`, `handoff` and `end` draw no control of their own.
 */
export function isBookingControl(action: ChatAction): boolean {
  switch (action.type) {
    case 'ask':
      return action.options.length > 0;
    case 'offer_slots':
    case 'collect_details':
    case 'confirm':
      return true;
    default:
      return false;
  }
}

// ── What a tap sends ────────────────────────────────────────────

export type BookingChoiceKind = 'option' | 'slot' | 'yes' | 'no';

export interface BookingChoice {
  kind: BookingChoiceKind;
  /** What the visitor reads: on the chip, then in their own bubble. */
  label: string;
  /** What goes on the wire as `message`: the option's value, the slot's instant, `yes` / `no` — verbatim. */
  message: string;
}

/** The chips and buttons the latest turn's actions draw, in the order the actions came. */
export function bookingChoices(
  actions: readonly ChatAction[],
  language: string,
  strings: Pick<WidgetStrings, 'bookingConfirmYes' | 'bookingConfirmNo'>,
): BookingChoice[] {
  const out: BookingChoice[] = [];
  for (const action of actions) {
    if (action.type === 'ask') {
      for (const option of action.options) {
        const label = action.field === 'date' ? formatDateLabel(option.value, language) : option.label || option.value;
        out.push({ kind: 'option', label, message: option.value });
      }
    } else if (action.type === 'offer_slots') {
      for (const slot of action.slots) {
        out.push({ kind: 'slot', label: formatSlotLabel(slot, language, action.timezone), message: slot });
      }
    } else if (action.type === 'confirm') {
      out.push({ kind: 'yes', label: strings.bookingConfirmYes, message: CONFIRM_YES });
      out.push({ kind: 'no', label: strings.bookingConfirmNo, message: CONFIRM_NO });
    }
  }
  return out;
}

// ── The details form ────────────────────────────────────────────

/** The booking's customer fields' limits: the details form's own (details.ts). */
export const BOOKING_DETAIL_LIMITS: Readonly<Record<DetailField, number>> = {
  customer_name: CONTACT_LIMITS.name,
  customer_email: CONTACT_LIMITS.email,
  customer_phone: CONTACT_LIMITS.phone,
};

/**
 * The `details` a submit sends: every field the form showed, trimmed. An
 * optional field left empty is left out (no `customer_phone: ''`), and a field
 * the form did not show is never sent, so it cannot blank something the
 * server already holds.
 */
export type BookingDetails = Partial<Record<DetailField, string>>;

export type BookingDetailsValidation =
  | { ok: true; value: BookingDetails }
  | { ok: false; field: DetailField; reason: 'required' | 'invalid' | 'tooLong' };

/**
 * The details form's own rules (details.ts), over the fields the server
 * listed, in its order; the first problem wins, as in lead.ts.
 */
export function validateBookingDetails(
  raw: Partial<Record<DetailField, string>>,
  fields: readonly DetailField[],
  required: readonly DetailField[],
): BookingDetailsValidation {
  const contact: ContactValues = {};
  for (const field of fields) contact[CONTACT_FIELD_OF[field]] = raw[field];
  const v = validateContact(
    contact,
    fields.map((field) => CONTACT_FIELD_OF[field]),
    required.map((field) => CONTACT_FIELD_OF[field]),
  );
  if (!v.ok) {
    const refused = DETAIL_FIELDS.find((field) => CONTACT_FIELD_OF[field] === v.field) as DetailField;
    return { ok: false, field: refused, reason: v.reason };
  }
  const value: BookingDetails = {};
  for (const field of fields) {
    const given = v.value[CONTACT_FIELD_OF[field]];
    if (given) value[field] = given;
  }
  return { ok: true, value };
}

/**
 * The visitor's bubble for a details submit, which is also its `message`:
 * "Booking under Ada". It never carries the email, and has words of its own
 * when the form asked for no name.
 */
export function bookingDetailsLine(
  details: BookingDetails,
  strings: Pick<WidgetStrings, 'bookingUnderName' | 'bookingDetailsSent'>,
): string {
  return details.customer_name ? fill(strings.bookingUnderName, { name: details.customer_name }) : strings.bookingDetailsSent;
}
